import { useAtom } from 'jotai';
import { useAuth } from '@/features/auth';
import { favoritesAtom, type Favorite, type FavoriteSubject, type StoredFavorite } from './store';
import { WORKSPACE_TYPES } from '@/features/workspaces/constants';
import { toProjectType } from '@/features/projects/utils';

const storedWorkspaceType = (type?: string) =>
  WORKSPACE_TYPES.find((known) => known.toLowerCase() === type?.toLowerCase());

const toFavorite = (stored: StoredFavorite): Favorite | undefined => {
  if (stored.subject === 'evaluation') {
    const type = storedWorkspaceType(stored.type);
    return type && { ...stored, subject: 'evaluation', type };
  }
  if (stored.subject === 'project') {
    return { ...stored, subject: 'project', type: toProjectType(stored.type) };
  }
  if (stored.subject === 'bid') return { ...stored, subject: 'bid' };
  if (stored.subject === 'artifact') return { ...stored, subject: 'artifact' };
  return { ...stored, subject: 'procurement' };
};

export const favoritesBySubject = <S extends FavoriteSubject>(
  favorites: Favorite[],
  subject: S,
): Extract<Favorite, { subject: S }>[] =>
  favorites.filter((fav): fav is Extract<Favorite, { subject: S }> => fav.subject === subject);

export function useFavorites() {
  const auth = useAuth();
  const scopeId = auth.user.organizationId;
  const [favoritesStorage, setFavoritesStorage] = useAtom(favoritesAtom);
  const storageKey = `${scopeId}-favorites`;
  const favorites = (favoritesStorage[storageKey] ?? []).flatMap(
    (stored) => toFavorite(stored) ?? [],
  );

  const isFavorited = (id: string) => favorites.some((fav) => fav.id === id);

  const toggleFavorite = (item: StoredFavorite) =>
    setFavoritesStorage((prev) => {
      const scopeFavorites = prev[storageKey] ?? [];
      return {
        ...prev,
        [storageKey]: scopeFavorites.some((fav) => fav.id === item.id)
          ? scopeFavorites.filter((fav) => fav.id !== item.id)
          : [...scopeFavorites, item],
      };
    });

  const removeFavorite = (id: string) =>
    setFavoritesStorage((prev) => ({
      ...prev,
      [storageKey]: (prev[storageKey] ?? []).filter((fav) => fav.id !== id),
    }));

  return { favorites, isFavorited, toggleFavorite, removeFavorite };
}
